import React from "react";
import EnhancedTextField from "../../../../../components/EnhancedTextField";
import EnhancedSelect from "../../../../../components/EnhancedSelect";
import {Box, Grid, InputLabel, styled, Typography, Tooltip} from "@mui/material";
import CatalogCheckbox from "../../../../../components/EnhancedCheckbox";
import EnhancedAutocomplete from "../../../../../components/EnhancedAutocomplete";
import {inputHeight} from "../../../../../theme";
import {EnhancedRte} from "../../../../../components/EnhancedRte";
import EnhancedVideoField from "../../../../../components/EnhancedVideoField";
import EnhancedDocumentField from "../../../../../components/EnhancedDocumentField";
import {useDebouncedEffect, useDidUpdateEffect, AI_ASSIST_FEATURES} from "../../../../../utils/utils";
import isEqual from "lodash.isequal";
import {isStrictlyRequired} from "../../import/product/components/AttributeMapping";
import Config from "../../../../../config";
import {AISparklesIcon} from '../../../../../components/AIStarsIcon';
import type {ProductAttribute} from "../types";

// region TypeScript Interfaces
interface StyledInputLabelProps {
    labelstyles?: React.CSSProperties;
    [key: string]: any;
}

export interface AttributeInputProps {
    attribute: ProductAttribute;
    error?: string;
    value: any;
    disabled?: boolean;
    onChange: (attributeName: string, value: any) => void;
    forceRefresh?: boolean;
    onAIEnrich?: (attributeName: string) => void;
    label?: React.ReactNode;
    labelstyles?: React.CSSProperties;
    required?: boolean;
    partnerId?: string | number;
    catalog?: string;
    isRetailer?: boolean;
    index?: number;
    helperText?: any;
}
// endregion

export const StyledInputLabel: React.ComponentType<StyledInputLabelProps> = styled(InputLabel)(({labelstyles}: any) => ({
    textAlign: "left",
    ...labelstyles
})) as any;

export const AttributeInput = React.memo((props: AttributeInputProps) => {
    const {attribute, error, value, disabled, onChange, forceRefresh, onAIEnrich} = props;
    const [localValue, setLocalValue] = React.useState<any>(value);
    const firstRender = React.useRef(true);
    const required = isStrictlyRequired(attribute.ValidationRules) || props.required;

    // Check if this field is eligible for AI enrichment (any free text attribute)
    const isAIEligible = AI_ASSIST_FEATURES() &&
                          attribute.Type === 'text' &&
                          !disabled &&
                          onAIEnrich;

    // Text field is debounced to prevent too many updates //
    // Same applies to RTE but its handled internally      //
    useDebouncedEffect(() => {
        if(firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (localValue === undefined) return;
        onChange(attribute.Name, localValue)
    }, [localValue], 500);

    // Reset local value if force refresh is triggered || attribute changes
    useDidUpdateEffect(() => {
        setLocalValue(value);
    }, [forceRefresh, attribute.Name]);
    ////////////////////////////////////////////////////////

    switch (attribute.Type) {
        case "text":
            if (attribute.Editor === "html")
                return (
                    <Grid style={{
                        maxHeight: "300px",
                        minHeight: "150px",
                        width: "100%",
                        margin: "5px 0",
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        flexDirection: "column"
                    }}>
                        <Box style={{ display: 'flex', alignItems: 'center', marginBottom: "5px", width: '100%' }}>
                            <StyledInputLabel labelstyles={props.labelstyles}
                                              error={!!error}
                                required={isAIEligible ? false : required}
                            >
                                {props.label}
                                {isAIEligible && required && (
                                    <span style={{ color: '#f44336' }}>&thinsp;*</span>
                                )}
                                {isAIEligible && (
                                    <Tooltip title={`Use AI to generate ${attribute.FriendlyName || attribute.Name}`}>
                                        <span
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAIEnrich!(attribute.Name); }}
                                            style={{ cursor: 'pointer', display: 'inline-flex', verticalAlign: 'middle', marginLeft: '4px', marginTop: '-2px', marginBottom: '-2px', pointerEvents: 'auto' }}
                                        >
                                            <AISparklesIcon size={20} />
                                        </span>
                                    </Tooltip>
                                )}
                            </StyledInputLabel>
                        </Box>
                        <EnhancedRte disabled={disabled}
                                     attribute={attribute} error={error}
                                     value={value} onChange={onChange}/>
                        {error && (
                            <Typography variant={"caption"} color={"error"}
                                        style={{textAlign: "left", margin: "3px 14px 0 14px"}}
                            >
                                {error}
                            </Typography>
                        )}
                    </Grid>
                );
            return (
                <EnhancedTextField
                    labelstyles={{textAlign: "left", ...props.labelstyles}}
                    error={error}
                    helperText={error}
                    multiline={attribute.Editor === "textarea"}
                    rows={attribute.Editor === "textarea" ? 3 : undefined}
                    label={(
                        <>
                            {props.label}
                            {isAIEligible && required && (
                                <span style={{ color: '#f44336' }}>&thinsp;*</span>
                            )}
                            {isAIEligible && (
                                <Tooltip title={`Use AI to generate ${attribute.FriendlyName || attribute.Name}`}>
                                    <span
                                        data-testid={`poc-product-attribute-ai-${attribute.Name}`}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAIEnrich!(attribute.Name); }}
                                        style={{ cursor: 'pointer', display: 'inline-flex', verticalAlign: 'middle', marginLeft: '4px', marginTop: '-2px', marginBottom: '-2px', pointerEvents: 'auto' }}
                                    >
                                        <AISparklesIcon size={20} />
                                    </span>
                                </Tooltip>
                            )}
                        </>
                    )}
                    value={localValue}
                    disabled={disabled}
                    inputProps={{"data-testid": `poc-product-attribute-input-${attribute.Name}`}}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalValue(e.target.value)}
                    required={isAIEligible ? false : required}
                />
            );
        case "select": {
            const selectOptions = attribute.Options === undefined
                ? []
                : attribute.Options.map((option: string) => {
                    return {value: option, label: option}
                })

            return (
                <Grid data-testid={`poc-product-attribute-select-${attribute.Name}`} style={{width: "100%"}}>
                <EnhancedSelect error={error} helperText={error} label={props.label} labelstyles={props.labelstyles}
                                isClearable
                                disabled={disabled}
                                value={{
                                    value: value,
                                    label: value
                                }}
                                options={selectOptions}
                                onChange={(e: any) => onChange(attribute.Name, e ? e.value : '')}
                                required={required}
                />
                </Grid>
            );
        }
        case "number":
            return (
                <EnhancedTextField labelstyles={props.labelstyles} label={props.label} value={value}
                                   type={"number"} disabled={disabled}
                                   inputProps={{"data-testid": `poc-product-attribute-input-${attribute.Name}`}}
                                   onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(attribute.Name, e.target.value)}
                                   required={required}
                                   error={error} helperText={error}
                />
            );
        case "yesno":
            return (
                <Grid item container justifyContent={"flex-start"} alignItems={"center"} style={{gap: "6px"}}>
                    {props.label && (
                        <StyledInputLabel labelstyles={props.labelstyles} style={{display: "flex"}}
                            required={required} error={error} helperText={error}
                        >
                            {props.label}:
                        </StyledInputLabel>
                    )}
                    <CatalogCheckbox
                        checked={value === "yes"} disabled={disabled}
                        onChange={() => onChange(attribute.Name, value === "yes" ? "no" : "yes")}
                    />
                    {error && (
                        <Typography variant={"caption"} color={"error"}>{error}</Typography>
                    )}
                </Grid>
            );
        case "list":
            return (
                <EnhancedAutocomplete
                    // See EnhancedAutocomplete.js for more details about this
                    createFieldSet={(hovering: boolean, menuOpen: boolean) => {
                        return {
                            height: inputHeight,
                            border: error ? "2px solid red"
                                : menuOpen
                                    ? "2px solid black"
                                    : hovering
                                        ? "1px solid black"
                                        : "1px solid rgba(0, 0, 0, 0.23)",
                            borderRadius: "6px",
                        }
                    }}
                    autocompletestyles={{
                        background: "white",
                        height: inputHeight,
                        overflow: "auto",
                        "& fieldset": {
                            border: "none !important"
                        },
                        "& .MuiAutocomplete-inputRoot": {
                            padding: 0,
                        },
                    }}
                    endadormentstyles={{
                        top: "19px",
                    }}
                    inputrootstyles={{
                        background: "transparent",
                        height: inputHeight,
                        overflow: "auto"
                    }}
                    disabled={disabled}
                    label={props.label}
                    labelstyles={props.labelstyles}
                    options={[]}
                    value={value}
                    onChange={(_e: any, value: any) => onChange(attribute.Name, value)}
                    required={required}
                    error={error}
                />
            )
        case "video":
            return (
                <EnhancedVideoField 
                    labelstyles={{textAlign: "left", ...props.labelstyles}} 
                    error={error} 
                    helperText={error}
                    label={props.label} 
                    value={localValue} 
                    disabled={disabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalValue(e.target.value)}
                    required={required}
                />
            );
        case "document":
            return (
                <EnhancedDocumentField 
                    labelstyles={{textAlign: "left", ...props.labelstyles}} 
                    error={error} 
                    helperText={error}
                    label={props.label} 
                    value={localValue} 
                    disabled={disabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalValue(e.target.value)}
                    required={required}
                    Config={Config}
                    partnerId={props.partnerId}
                    catalog={props.catalog}
                    isRetailer={props.isRetailer}
                />
            );
        default:
            return (
                <Box>
                    <Typography>DEFAULT - {attribute.Name} - {attribute.Type}</Typography>
                </Box>
            );
    }
}, (prevProps, nextProps) => {
    return isEqual(prevProps.attribute, nextProps.attribute) &&
        prevProps.error === nextProps.error &&
        ((prevProps.attribute.Type === "text" || prevProps.attribute.Type === "video" || prevProps.attribute.Type === "document") ? true : prevProps.value === nextProps.value) && // Text, video and document field state is handled internally
        prevProps.disabled === nextProps.disabled &&
        prevProps.label === nextProps.label &&
        isEqual(prevProps.labelstyles, nextProps.labelstyles) &&
        prevProps.onChange === nextProps.onChange &&
        prevProps.forceRefresh === nextProps.forceRefresh;
});
